import React, { useState } from "react";
import moment from "moment";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from "recharts";

import {
  GlobalStyle,
  TargetBox,
  TargetBoxTitle,
  TargetBoxValue,
  TooltipBox
} from "./LinearChart.styled";

import DataVizStore from "../../../../../Stores/DataVizStore";
import { withRefluxStore } from "../../../../../Stores";
import { formatIntlNumber, numberFormat } from "../../../../../utils/Helpers";

const formatDate = (dateString) => {
  return moment(dateString).format("DD. MMM.");
};

const gradientOffset = (data) => {
  const dataAboveThreshold = data.filter((metric) => metric.ctr >= 1);
  if (dataAboveThreshold.length === 0) {
    return 1;
  }

  const dataMax = Math.max(...dataAboveThreshold.map((metric) => metric.ctr));
  const dataMin = Math.min(...dataAboveThreshold.map((metric) => metric.ctr));

  return (dataMax - 1) / (dataMax - dataMin);
};

const ChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const {
      name,
      value,
      payload: { language }
    } = payload[0];
    const isPercentage = name === "ctr";

    const score = formatIntlNumber(value, {
      language,
      points: isPercentage ? 2 : 0
    });

    return (
      <TooltipBox>
        <p>{`Date: ${label}`}</p>
        <p>
          <span style={{ textTransform: isPercentage ? "uppercase" : "capitalize" }}>{name}: </span>
          {score} {isPercentage && "%"}
        </p>
      </TooltipBox>
    );
  }
  return null;
};

const LinearChart = ({ data = [], selectedOptions, selectedChannel, cardKey, userProfile }) => {
  const [hoveredDate, setHoveredDate] = useState(null);

  const selectedCardOption = selectedOptions[cardKey] || "ctr";
  const selectedChannelOption = selectedChannel[cardKey] || "allChannelsAverage";

  const linearChartData = data[selectedChannelOption]
    ? data[selectedChannelOption].map((item) => ({
        ...item,
        date: formatDate(item.date),
        ctr: parseFloat(item.ctr).toFixed(2),
        language: userProfile?.language
      }))
    : [];

  const off = gradientOffset(linearChartData);

  const handleMouseMove = ({ isTooltipActive, activePayload }) => {
    if (isTooltipActive) {
      if (activePayload && activePayload.length) {
        const activeDate = activePayload[0].payload.date;
        setHoveredDate(activeDate);
      }
    } else {
      setHoveredDate(null);
    }
  };

  const showYAxisTicks = selectedCardOption === "ctr";

  return (
    <div style={{ width: "100%", height: 300 }}>
      <GlobalStyle />
      <ResponsiveContainer>
        <AreaChart
          width={500}
          height={400}
          data={linearChartData}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoveredDate(null)}
        >
          <XAxis
            dataKey="date"
            interval="preserveStartEnd"
            stroke="#7A89F4"
            tick={({ payload, x, y }) => {
              const isHovered = payload.value === hoveredDate;
              return (
                <text
                  x={x}
                  y={y + 10}
                  fill={isHovered ? "#1E1F43" : "#7A89F4"}
                  textAnchor="middle"
                  fontWeight={isHovered ? "700" : "normal"}
                >
                  {payload.value}
                </text>
              );
            }}
          />
          <YAxis
            ticks={showYAxisTicks ? [1] : []}
            tickLine={false}
            axisLine={false}
            hide={!showYAxisTicks}
            padding={0}
            tickHeight={35}
            tickWidth={55}
            tick={({ payload, y }) => {
              return (
                <foreignObject x={0} y={y - 17} width={55} height={35}>
                  <TargetBox>
                    <TargetBoxTitle>Target</TargetBoxTitle>
                    <TargetBoxValue>{`${numberFormat(payload.value)}%`}</TargetBoxValue>
                  </TargetBox>
                </foreignObject>
              );
            }}
          />
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={"#ABB4F4"} />
          <Tooltip content={<ChartTooltip />} />
          <defs>
            <linearGradient id="splitColor" x1="0" y1="0" x2="0" y2="1">
              <stop offset={"1%"} stopColor="#36F99F" stopOpacity={1} />
              <stop offset={off} stopColor="#1E22FF" stopOpacity={1} />
            </linearGradient>
          </defs>
          <Area
            type="linear"
            dataKey={selectedCardOption}
            stroke="url(#splitColor)"
            strokeWidth={3}
            fill="url(#splitColor)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default withRefluxStore(
  LinearChart,
  [DataVizStore],
  ["selectedOptions", "selectedChannel", "userProfile"]
);
